import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import './Sleep.css';
import buttonImg from '../assets/sleep/button.png';
import boxImg    from '../assets/sleep/box.png';

interface CharacterStats {
  health:     number;
  max_health: number;
}

export default function SleepPage() {
  const [stats, setStats]               = useState<CharacterStats | null>(null);
  const [confirming, setConfirming]     = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const [wakeUpAt, setWakeUpAt]         = useState<number | null>(null);
  const timerRef = useRef<number | null>(null);

  // On mount: fetch stats then sleep status
  useEffect(() => {
    api.get<{ health: number; max_health: number }>('/character')
      .then(r => setStats({ health: r.data.health, max_health: r.data.max_health }))
      .catch(console.error)
      .finally(() => {
        api.get<{ status: { remainingSec: number; wakeUpAt: number|null } }>('/sleep/status')
          .then(r => {
            const { remainingSec, wakeUpAt } = r.data.status;
            if (remainingSec > 0) {
              setRemainingSec(remainingSec);
              setWakeUpAt(wakeUpAt);
            }
          })
          .catch(console.error);
      });
  }, []);

  const missingHp = stats ? stats.max_health - stats.health : 0;
  const sleepMin  = Math.ceil(missingHp);

  // Begin sleep
  const doSleep = async () => {
    setConfirming(false);
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<{ sleep: {
        missingHp: number;
        durationSec: number;
        wakeUpAt: number;
      } }>('/sleep/start');
      const info = res.data.sleep;
      setRemainingSec(info.durationSec);
      setWakeUpAt(info.wakeUpAt);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Stop sleep immediately
  const stopSleeping = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{
        player: { health: number; max_health: number; healedHp: number; elapsedSec: number }
      }>('/sleep/stop');
      // Optionally show healedHp or elapsedSec:
      console.log(`Healed ${res.data.player.healedHp} HP over ${res.data.player.elapsedSec}s`);
      window.location.reload();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (remainingSec === null) return;
    if (remainingSec <= 0) {
      window.location.reload();
      return;
    }
    timerRef.current = window.setInterval(() => {
      setRemainingSec(sec => {
        if (sec === null || sec <= 1) {
          if (timerRef.current !== null) clearInterval(timerRef.current);
          return 0;
        }
        return sec - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, [remainingSec]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s}s`;
  };

  return (
    <div className="sleep-page">
      <div className="sleep-container">
        <img src={boxImg} className="sleep-box" alt="Sleep Box" />
        <div className="sleep-content">
          {remainingSec !== null ? (
            <>
              <h2>Sleeping…</h2>
              <p className="sleep-timer">{formatTime(remainingSec)}</p>
              {wakeUpAt && (
                <p className="sleep-wakeup">
                  Wake up at {new Date(wakeUpAt * 1000).toLocaleTimeString()}
                </p>
              )}
              <button
                className="sleep-btn wakeup"
                onClick={stopSleeping}
                disabled={loading}
              >
                {loading ? '…' : 'Wake Up'}
              </button>
            </>
          ) : (
            <>
              <h2>Rest</h2>
              {stats && stats.health < stats.max_health ? (
                <>
                  <p>
                    You need <strong>{missingHp}</strong> HP (≈ <strong>{sleepMin} min</strong>) to fully heal.
                  </p>
                  <p>All activities will be blocked until you wake up.</p>
                  {error && <p className="sleep-error">{error}</p>}
                  <button
                    className="sleep-btn"
                    style={{ backgroundImage: `url(${buttonImg})` }}
                    onClick={() => setConfirming(true)}
                    disabled={loading}
                  >
                    {loading ? '…' : 'Sleep'}
                  </button>
                </>
              ) : (
                <p>You are already at full health.</p>
              )}
            </>
          )}
        </div>
      </div>

      {confirming && stats && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>
              Really sleep for <strong>{sleepMin} minute{sleepMin !== 1 ? 's' : ''}</strong>, blocking all activities until full health?
            </p>
            <div className="confirm-buttons">
              <button onClick={doSleep}>Yes</button>
              <button onClick={() => setConfirming(false)}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
